/**
 * @file Axios 请求封装
 * @description 包含基础配置、请求/响应拦截、参数过滤、请求头注入、跨域支持、统一错误处理
 */

import axios from 'axios'
import { Message } from 'element-ui'
import { getToken, removeToken } from '@/utils/auth'

// 创建 axios 实例
const service = axios.create({
  // 基础路径，在 .env 中配置 VUE_APP_BASE_API
  baseURL: process.env.VUE_APP_BASE_API || '/api',
  // 超时时间 15s
  timeout: 15000,
  // 允许跨域携带 cookie / auth 头
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
    'X-Requested-With': 'XMLHttpRequest'
  }
})

/**
 * 过滤请求参数
 * @param {Object} params - 原始参数
 * @returns {Object} - 过滤后的参数
 * @description 删除 null、undefined、空字符串；可选递归处理对象
 */
export function filterParams(params = {}, deep = false) {
  if (params === null || params === undefined) return {}
  if (typeof params !== 'object') return params

  const filtered = {}
  for (const key of Object.keys(params)) {
    const value = params[key]

    // 过滤 null、undefined、空字符串
    if (value === null || value === undefined || value === '') continue

    // 数组：过滤内部无效项
    if (Array.isArray(value)) {
      filtered[key] = value.filter(item => item !== null && item !== undefined && item !== '')
      continue
    }

    // 对象：按需递归过滤
    if (deep && typeof value === 'object' && !(value instanceof Date) && !(value instanceof File)) {
      filtered[key] = filterParams(value, true)
      continue
    }

    filtered[key] = value
  }
  return filtered
}

/**
 * 简单的请求参数校验
 * @param {Object} params - 请求参数
 * @param {Array<{key, required, validator, message}>} rules - 校验规则
 * @returns {string|null} - 错误信息或 null
 */
export function validateParams(params = {}, rules = []) {
  for (const rule of rules) {
    const { key, required, validator, message } = rule
    const value = params[key]

    if (required && (value === undefined || value === null || value === '')) {
      return message || `参数 ${key} 不能为空`
    }

    if (validator && value !== undefined && value !== null) {
      const result = validator(value)
      if (result === false || typeof result === 'string') {
        return result || message || `参数 ${key} 校验失败`
      }
    }
  }
  return null
}

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 1. 注入 Token
    const token = getToken()
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }

    // 2. 动态注入租户ID / 语言 / 设备标识等通用头
    config.headers['X-Tenant-Id'] = localStorage.getItem('tenantId') || ''
    config.headers['Accept-Language'] = localStorage.getItem('locale') || 'zh-CN'

    // 3. 过滤 GET/DELETE params、POST/PUT data
    if (config.params) {
      config.params = filterParams(config.params, true)
    }
    if (config.data && config.headers['Content-Type'].includes('application/json')) {
      config.data = filterParams(config.data, true)
    }

    // 4. 参数校验（如配置中传入 validateRules）
    if (config.validateRules) {
      const source = config.method === 'get' || config.method === 'delete'
        ? config.params
        : config.data
      const error = validateParams(source, config.validateRules)
      if (error) {
        return Promise.reject(new Error(error))
      }
    }

    // 5. 请求日志（开发环境）
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log(`[Request] ${config.method?.toUpperCase()} ${config.url}`, config)
    }

    return config
  },
  error => {
    Message.error('请求发送失败，请检查网络')
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    const res = response.data

    // 根据后端约定码处理，例如：20000 为成功，50008 token 失效等
    const SUCCESS_CODE = 200
    const LOGIN_EXPIRED_CODE = 401

    // 兼容纯数据返回（如文件流、第三方接口）
    if (res === undefined || res === null || typeof res !== 'object') {
      return res
    }

    if (res.code !== undefined && res.code !== SUCCESS_CODE) {
      Message.error(res.message || '请求失败')

      // Token 过期/登录失效
      if (res.code === LOGIN_EXPIRED_CODE) {
        removeToken()
        window.location.href = '/login'
      }

      return Promise.reject(new Error(res.message || '请求失败'))
    }

    return res.data === undefined ? res : res.data
  },
  error => {
    const { response, message } = error

    if (response) {
      const status = response.status
      const msgMap = {
        400: '请求参数错误',
        401: '登录已过期，请重新登录',
        403: '没有权限访问',
        404: '请求资源不存在',
        405: '请求方法不允许',
        408: '请求超时',
        500: '服务器内部错误',
        502: '网关错误',
        503: '服务不可用',
        504: '网关超时'
      }
      const msg = response.data?.message || msgMap[status] || `请求失败: ${status}`
      Message.error(msg)

      if (status === 401) {
        removeToken()
        window.location.href = '/login'
      }
    } else if (message && message.includes('timeout')) {
      Message.error('请求超时，请稍后重试')
    } else {
      Message.error('网络异常，请检查网络连接')
    }

    return Promise.reject(error)
  }
)

/**
 * 通用请求方法（支持额外配置）
 * @param {Object} config - axios 配置
 */
export function request(config) {
  return service(config)
}

/**
 * GET 请求
 */
export function get(url, params = {}, config = {}) {
  return service({
    url,
    method: 'get',
    params,
    ...config
  })
}

/**
 * POST 请求
 */
export function post(url, data = {}, config = {}) {
  return service({
    url,
    method: 'post',
    data,
    ...config
  })
}

/**
 * PUT 请求
 */
export function put(url, data = {}, config = {}) {
  return service({
    url,
    method: 'put',
    data,
    ...config
  })
}

/**
 * DELETE 请求
 */
export function del(url, params = {}, config = {}) {
  return service({
    url,
    method: 'delete',
    params,
    ...config
  })
}

/**
 * 上传文件
 * @param {string} url - 上传地址
 * @param {File} file - 文件对象
 * @param {Object} extra - 额外参数
 */
export function upload(url, file, extra = {}, config = {}) {
  const formData = new FormData()
  formData.append('file', file)
  Object.keys(extra).forEach(key => {
    formData.append(key, extra[key])
  })

  return service({
    url,
    method: 'post',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    ...config
  })
}

export default service
